import { Auth0Client, Auth0ClientOptions } from "@auth0/auth0-spa-js";
import { Config } from "../entities/config";
import { navStateStore } from "../state/nav-state-store";
import logger from "./logger";
import windowService from "./window-service";

const isWwwProductionRegex = /(https:\/\/www).(porsche).(com|cn)/;
const sameOriginPlaceholder = "SAME_ORIGIN";

export interface AuthLocaleOptions {
  ui_locales: string;
  "ext-country": string;
  "ext-language": string;
}

// Wrapper function for better testability
/* istanbul ignore next */
export function getAuth0Client(config: Config): Auth0Client {
  const authConfig: Auth0ClientOptions = {
    clientId: config.IDENTITY_CLIENT_ID,
    domain: config.IDENTITY_PROVIDER_URL,
    authorizationParams: {
      audience: config.IDENTITY_AUDIENCE,
      scope: config.IDENTITY_USER_SCOPES,
    },
  };

  return new Auth0Client(authConfig);
}

/**
 * Be aware, that redirects must be whitelisted with the IDP
 * It allows wildcards for subdomains (*.domain.com), but only on preview
 *
 * On www.porsche.(com|cn) pages, we can host our own auth-callback via delivery gateway to reconstruct the path.
 * auth/callback.html becomes the default.
 * On non www pages e.g. foo.porsche.(com|cn), the main page of www.porsche.(com|cn) is the default.
 *
 * The callback can always be overwritten via config.
 * In such a case, we must add the requested origin to our client to be whitelisted.
 * When not overwritten and not under wwww, the main www page is used still as a last resort.
 *
 * Because the IDP allows wildcards on preview, we can define the redirect to SAME_ORIGIN here.
 */
export function getRedirectUrl(
  configRedirect?: string | undefined,
  overwrittenRedirect?: string | undefined,
): string {
  if (!windowService.location) {
    logger.error(
      "PHN_NO_LOCATION",
      new Error("location not present for generating auth redirects"),
    );
    return "https://www.porsche.com/";
  }

  const { origin } = windowService.location;

  let redirectUrl = origin;

  if (isWwwProductionRegex.exec(origin)) {
    redirectUrl = `${redirectUrl}/auth/callback.html`;
  }

  if (configRedirect) {
    if (origin.includes("porsche.cn")) {
      configRedirect = configRedirect.replace("porsche.com", "porsche.cn");
    }

    if (configRedirect === sameOriginPlaceholder) {
      configRedirect = origin;
    }

    redirectUrl = configRedirect;
  }

  if (overwrittenRedirect) {
    redirectUrl = overwrittenRedirect;

    if (overwrittenRedirect === sameOriginPlaceholder) {
      redirectUrl = origin;
    }
  }

  return redirectUrl;
}

export const defaultAuthLocaleOptions: AuthLocaleOptions = {
  ui_locales: "en",
  "ext-country": "gb",
  "ext-language": "en_GB",
};

export function mapAuthLocaleOptions(locale: string): AuthLocaleOptions {
  if (!locale || locale === "") {
    return defaultAuthLocaleOptions;
  }

  try {
    const sanitizedLocale: string = locale.replace(/_/g, "-");

    const localeSplit = sanitizedLocale.split("-")[0];
    const countrySplit = sanitizedLocale.split("-")[1];
    const localesMapped = {
      ui_locales: localeSplit.toLowerCase(),
      "ext-country": countrySplit.toLowerCase(),
      "ext-language": sanitizedLocale.replace(/-/g, "_"),
    };

    return localesMapped;
  } catch (e) {
    logger.error(
      "PHN_AUTH_LOCALE_OPTIONS",
      new Error(`Error while parsing locale ${locale}`),
    );

    return defaultAuthLocaleOptions;
  }
}

export function getLogoutUrl(config: Config): string {
  const idpDefaultLogout = `${config.IDENTITY_PROVIDER_URL}/v2/logout`;
  const logoutCallbackConfig = config.IDENTITY_LOGOUT_CALLBACK;
  const logoutCallbackOverwritten =
    navStateStore.state.authOptions?.logoutCallback;

  if (!windowService.location) {
    logger.error(
      "PHN_NO_LOCATION",
      new Error("location not present for generating auth logout"),
    );
    return idpDefaultLogout;
  }

  if (!config.IDENTITY_CLIENT_ID) {
    return idpDefaultLogout;
  }

  const { href, origin } = windowService.location;

  let logoutUrl = buildLogoutUrl(config, origin);

  if (isWwwProductionRegex.exec(origin)) {
    logoutUrl = buildLogoutUrlWithCallback(
      config,
      `${origin}/auth/logout.html`,
      href,
    );
  }

  if (logoutCallbackConfig) {
    if (origin.includes("porsche.cn")) {
      logoutUrl = buildLogoutUrlWithCallback(
        config,
        logoutCallbackConfig.replace("porsche.com", "porsche.cn"),
        href,
      );
    } else {
      logoutUrl = buildLogoutUrlWithCallback(
        config,
        logoutCallbackConfig,
        href,
      );
    }

    if (logoutCallbackConfig === sameOriginPlaceholder) {
      logoutUrl = buildLogoutUrl(config, origin);
    }
  }

  if (logoutCallbackOverwritten) {
    logoutUrl = buildLogoutUrlWithCallback(
      config,
      logoutCallbackOverwritten,
      href,
    );

    if (logoutCallbackOverwritten === sameOriginPlaceholder) {
      logoutUrl = buildLogoutUrl(config, origin);
    }
  }

  return logoutUrl;
}

function buildLogoutUrl(config: Config, returnTo: string): string {
  return `${config.IDENTITY_PROVIDER_URL}/v2/logout?client_id=${config.IDENTITY_CLIENT_ID}&returnTo=${returnTo}`;
}

function buildLogoutUrlWithCallback(
  config: Config,
  logoutCallback: string,
  href: string,
): string {
  const returnViaCallback = `${logoutCallback}?redirectUrl=${encodeURIComponent(href)}`;
  return `${config.IDENTITY_PROVIDER_URL}/v2/logout?client_id=${config.IDENTITY_CLIENT_ID}&returnTo=${returnViaCallback}`;
}
